import type {
  AuthSubsidiary,
  AuthBranch,
  AuthDrawer,
  AuthUser,
  AuthSessionInfo,
} from './cashierAuthClient';

export class AuthApiError extends Error {
  status: number;
  code: string;
  /** The whole error body — SETUP_REQUIRED carries the wizard's token in it. */
  body: Record<string, unknown> | null;
  constructor(status: number, code: string, body: Record<string, unknown> | null = null) {
    super(`${code} (HTTP ${status})`);
    this.name = 'AuthApiError';
    this.status = status;
    this.code = code;
    this.body = body;
  }
}

export interface LoginRequest {
  tenant_slug: string;
  /**
   * The till does NOT send one — its login screen has no username field and
   * the server identifies the cashier from the password. Kept on the type
   * because the endpoint still accepts it from anything else.
   */
  username?: string;
  password: string;
  terminal_code?: string;
}

export interface LoginResponse {
  pending_token: string;
  user: AuthUser;
  available_subsidiaries: AuthSubsidiary[];
}

export async function loginRequest(body: LoginRequest): Promise<LoginResponse> {
  return postJson<LoginResponse>('/api/v1/cashier/auth/login', body);
}

export interface SelectSubsidiaryResponse {
  pending_token: string;
  subsidiary: AuthSubsidiary;
  available_branches: AuthBranch[];
}

export async function selectSubsidiaryRequest(
  pendingToken: string,
  subsidiaryId: number,
): Promise<SelectSubsidiaryResponse> {
  return postJson<SelectSubsidiaryResponse>(
    '/api/v1/cashier/auth/select-subsidiary',
    { subsidiary_id: subsidiaryId },
    pendingToken,
  );
}

export interface SelectBranchResponse {
  pending_token: string;
  branch: AuthBranch;
  available_cash_drawers: AuthDrawer[];
}

export async function selectBranchRequest(
  pendingToken: string,
  branchId: number,
): Promise<SelectBranchResponse> {
  return postJson<SelectBranchResponse>(
    '/api/v1/cashier/auth/select-branch',
    { branch_id: branchId },
    pendingToken,
  );
}

export interface SelectDrawerResponse {
  token: string;
  expires_at: string;
  session: AuthSessionInfo;
}

export async function selectDrawerRequest(
  pendingToken: string,
  drawerId: number,
): Promise<SelectDrawerResponse> {
  return postJson<SelectDrawerResponse>(
    '/api/v1/cashier/auth/select-drawer',
    { cash_drawer_id: drawerId },
    pendingToken,
  );
}

async function postJson<T>(path: string, body: unknown, bearer?: string): Promise<T> {
  const headers: Record<string, string> = {
    'content-type': 'application/json',
    accept: 'application/json',
  };
  if (bearer) headers.authorization = `Bearer ${bearer}`;

  const resp = await fetch(path, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });
  if (resp.ok) return (await resp.json()) as T;

  let bodyJson: { error?: string } | null = null;
  try {
    bodyJson = (await resp.json()) as { error?: string };
  } catch { /* not JSON */ }
  throw new AuthApiError(
    resp.status,
    bodyJson?.error ?? `HTTP_${resp.status}`,
    bodyJson as Record<string, unknown> | null,
  );
}
